import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Users, TrendingUp, GraduationCap } from "lucide-react";
import { api, errMsg, FIELD, AREA } from "@/lib/api";
import { POSITIONS } from "@/lib/site";
import { PageHero, Reveal, SectionHeading } from "@/components/common/Reveal";
import { Field } from "@/components/common/Field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PERKS = [
  { icon: Users, title: "Good People", text: "Join a team that leads with integrity and heart." },
  { icon: TrendingUp, title: "Growth", text: "Build skills in tax, finance and business strategy." },
  { icon: GraduationCap, title: "Learning", text: "Ongoing education through the B2R Academy." },
];

const EMPTY = { full_name: "", email: "", phone: "", position: "", experience: "", licensed: false, availability: "", why_b2r: "", linkedin: "" };

const ApplicationForm = () => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.position) return toast.error("Please choose a position of interest.");
    setBusy(true);
    try {
      await api.post("/applications", f);
      setDone(true);
      toast.success("Application received!");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) return (
    <div className="lux-card p-12 text-center" data-testid="application-success">
      <CheckCircle2 className="w-14 h-14 text-[#D4AF37] mx-auto" />
      <h3 className="mt-6 text-4xl font-medium">Welcome to the conversation.</h3>
      <p className="mt-4 text-white/65">Thank you for applying to join the B2R Team. We'll review your application and be in touch.</p>
    </div>
  );

  return (
    <form onSubmit={submit} className="lux-card p-8 md:p-12 grid sm:grid-cols-2 gap-6" data-testid="application-form">
      <Field label="Full Name" required><Input className={FIELD} required value={f.full_name} onChange={set("full_name")} data-testid="application-name-input" /></Field>
      <Field label="Email" required><Input className={FIELD} type="email" required value={f.email} onChange={set("email")} data-testid="application-email-input" /></Field>
      <Field label="Phone" required><Input className={FIELD} required value={f.phone} onChange={set("phone")} data-testid="application-phone-input" /></Field>
      <Field label="Position of Interest" required>
        <Select value={f.position} onValueChange={set("position")}>
          <SelectTrigger className={FIELD} data-testid="application-position-select"><SelectValue placeholder="Select a position" /></SelectTrigger>
          <SelectContent>{POSITIONS.map((p) => <SelectItem key={p} value={p} data-testid={`position-option-${p.split(" ")[0].toLowerCase()}`}>{p}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Years of Experience">
        <Select value={f.experience} onValueChange={set("experience")}>
          <SelectTrigger className={FIELD} data-testid="application-experience-select"><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>{["None — eager to learn", "Less than 1 year", "1–3 years", "3–5 years", "5+ years"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Availability"><Input className={FIELD} placeholder="Full-time, part-time, seasonal…" value={f.availability} onChange={set("availability")} data-testid="application-availability-input" /></Field>
      <Field label="LinkedIn / Portfolio URL" className="sm:col-span-2"><Input className={FIELD} value={f.linkedin} onChange={set("linkedin")} data-testid="application-linkedin-input" /></Field>
      <Field label="Why B2R?" required className="sm:col-span-2"><Textarea className={AREA} required minLength={10} value={f.why_b2r} onChange={set("why_b2r")} data-testid="application-why-input" /></Field>
      <label className="sm:col-span-2 flex items-center gap-3 text-sm text-white/75">
        <Checkbox checked={f.licensed} onCheckedChange={(v) => setF({ ...f, licensed: !!v })} data-testid="application-licensed-checkbox" className="border-[#D4AF37] data-[state=checked]:bg-[#D4AF37]" />
        I currently hold a PTIN or relevant professional credential
      </label>
      <div className="sm:col-span-2"><button className="btn-gold" disabled={busy} data-testid="application-submit-button">{busy ? "Submitting…" : "Submit Application"}</button></div>
    </form>
  );
};

const OPPORTUNITIES = ["Tax Careers", "Software Packages", "Mentorship", "Business Opportunities", "Client Services"];

const Opportunities = () => (
  <section className="bg-black py-20 md:py-28" data-testid="careers-opportunities">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 md:gap-16 items-center">
      <Reveal className="md:col-span-5">
        <img src="/images/banner.jpg" srcSet="/images/banner-m.jpg 520w, /images/banner.jpg 816w" sizes="(max-width: 768px) 90vw, 40vw" loading="lazy"
          alt="Welcome to Baggs 2 Riches LLC — Learn, Earn, Grow banner" className="w-full max-w-sm mx-auto rounded-[1.5rem] border border-[#D4AF37]/35 purple-glow" data-testid="careers-banner" />
      </Reveal>
      <div className="md:col-span-7">
        <SectionHeading eyebrow="Learn ~ Earn ~ Grow" title={<>Same industry. <span className="italic gold-text">Bigger opportunities.</span></>}
          sub="Whether you're starting a tax career or building your own business, B2R gives you the tools, training and support to win — tax season and beyond." />
        <Reveal delay={0.15}>
          <ul className="mt-8 space-y-3">
            {OPPORTUNITIES.map((o) => (
              <li key={o} className="flex items-center gap-3 border-b border-white/10 pb-3 text-base text-white/85"><CheckCircle2 className="w-5 h-5 text-[#D4AF37] shrink-0" />{o}</li>
            ))}
          </ul>
          <div className="mt-10 flex flex-wrap gap-4">
            <a href="#apply" className="btn-gold" data-testid="careers-apply-now">Apply Now</a>
            <Link to="/academy" className="btn-outline-gold" data-testid="careers-classes-link">View Classes</Link>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export default function Careers() {
  return (
    <div data-testid="careers-page">
      <PageHero testid="careers-hero" eyebrow="Join the B2R Team" title={<>Build wealth. Build people. <span className="italic gold-text">Build with us.</span></>}
        sub="We're always looking for simply good people who want to provide exceptional service and grow with a company built on strategy." />
      <Opportunities />
      <section className="bg-[#0A0A0A] py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-3 gap-6">
          {PERKS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.1}><div className="lux-card lift p-8 h-full"><p.icon className="w-8 h-8 text-[#D4AF37]" strokeWidth={1.3} /><h3 className="mt-6 text-3xl font-medium">{p.title}</h3><p className="mt-3 text-sm text-white/60">{p.text}</p></div></Reveal>
          ))}
        </div>
      </section>
      <section id="apply" className="bg-black py-24 md:py-32 scroll-mt-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <SectionHeading eyebrow="Apply Now" title="Your application" sub="Tell us about yourself. All fields marked * are required." className="mb-12" />
          <ApplicationForm />
        </div>
      </section>
    </div>
  );
}
